import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import MonacoEditor from "@monaco-editor/react"
import fs from "fs"
import path from "path"
import { useRecoilState, useRecoilValue } from "recoil"

import { usePlugins } from "../../../recoil/pluginStudio/hooks"
import { useCodeTranspiler } from "../../../recoil/pluginStudio/hooks/useCodeTranspiler"
import {
  activeFileSelector,
  activePluginSelector,
  pluginStudioStateSelector,
} from "../../../recoil/pluginStudio/selectors"
import {
  CodeEditorPanelProps,
  CodeEditorState,
  ErrorSeverity,
  EventPluginData,
  getEffectiveViewRole,
  hasTranspiledContent,
  isCustomView,
  Plugin,
  PluginError,
  PluginErrorType,
  PluginFile,
  TranspileStatus,
  UserPluginData,
  UserRole,
  ViewFileName,
} from "../../../recoil/pluginStudio/types"
import {
  getFileContent,
  getTranspiledContent,
  updateCodeContent,
  updateTranspiledContent,
} from "../../../recoil/pluginStudio/utils/codeHelpers"
// Import from the local hooks directory
import { usePluginEditor } from "../hooks/usePluginEditor"
import { generatePluginTypeDefinitions } from "../types/pluginEditorTypes"
import { transpileTypeScript } from "../utils/codeTranspiler"
import { CustomViewsDropdown } from "./CustomViewsDropdown"
import ErrorDisplay from "./ErrorDisplay"

// Debug mode toggle for this file
const DEBUG_MODE = true

// Helper function to get file name based on role
const getFileNameFromRole = (role: UserRole): ViewFileName => {
  switch (role) {
    case UserRole.GUEST:
      return ViewFileName.GUEST
    case UserRole.HOST_EVENT_VIEW:
      return ViewFileName.HOST_EVENT_VIEW
    case UserRole.HOST_CONFIG_VIEW:
      return ViewFileName.HOST_CONFIG_VIEW
    default:
      return ViewFileName.UNKNOWN
  }
}

/**
 * Editor Breadcrumbs Component
 * Shows the current file path in the editor
 */
const EditorBreadcrumbs: React.FC<{
  viewType: UserRole
}> = ({ viewType }) => {
  // Fetch active plugin directly from Recoil
  const activePlugin = useRecoilValue(activePluginSelector)

  // Generate file path for breadcrumbs
  const getFilePath = () => {
    if (!activePlugin) return ""

    // For custom views, show the actual file name
    if (viewType === UserRole.CUSTOM) {
      // Find the active custom file
      const customFile = activePlugin.files?.find((file) => isCustomView(file))
      if (customFile) {
        return `${activePlugin.name || "Untitled Plugin"} / frontend / ${customFile.name}`
      }
    }

    // For standard views, use the mapped file name
    const fileName = getFileNameFromRole(viewType)
    return `${activePlugin.name || "Untitled Plugin"} / frontend / ${fileName}`
  }

  return (
    <div className="flex items-center text-xs text-gray-400">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-3.5 w-3.5 mr-1 text-blue-400"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fillRule="evenodd"
          d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"
          clipRule="evenodd"
        />
      </svg>
      <span>{getFilePath()}</span>
    </div>
  )
}

/**
 * Language Toggle Component
 * Toggles between TypeScript and JavaScript views
 */
const LanguageToggle: React.FC<{
  showTranspiledCode: boolean
  onToggleTranspiledView: () => void
}> = ({ showTranspiledCode, onToggleTranspiledView }) => {
  return (
    <div className="flex items-center">
      <span
        className={`px-2 py-0.5 text-xs rounded-l cursor-pointer ${
          !showTranspiledCode
            ? "bg-blue-600 text-white"
            : "bg-gray-700 text-gray-400 hover:bg-gray-600"
        }`}
        onClick={() => showTranspiledCode && onToggleTranspiledView()}
      >
        TS
      </span>
      <span
        className={`px-2 py-0.5 text-xs rounded-r cursor-pointer ${
          showTranspiledCode
            ? "bg-blue-600 text-white"
            : "bg-gray-700 text-gray-400 hover:bg-gray-600"
        }`}
        onClick={() => !showTranspiledCode && onToggleTranspiledView()}
      >
        JS
      </span>
    </div>
  )
}

/**
 * View Selector Component
 * Allows switching between Guest, Host Event, and Host Config views
 */
const ViewSelector: React.FC<{
  viewType: UserRole
  onCodeTabChange: (tab: UserRole | string, file?: any) => void
}> = ({ viewType, onCodeTabChange }) => {
  return (
    <div className="flex items-center space-x-2">
      <div className="flex bg-gray-700 rounded overflow-hidden">
        <button
          className={`px-2 py-1 text-xs ${
            viewType === UserRole.GUEST
              ? "bg-blue-600 text-white"
              : "text-gray-300 hover:bg-gray-600"
          }`}
          onClick={() => onCodeTabChange(UserRole.GUEST)}
        >
          Guest
        </button>
        <button
          className={`px-2 py-1 text-xs ${
            viewType === UserRole.HOST_EVENT_VIEW
              ? "bg-blue-600 text-white"
              : "text-gray-300 hover:bg-gray-600"
          }`}
          onClick={() => onCodeTabChange(UserRole.HOST_EVENT_VIEW)}
        >
          Host
        </button>
        <button
          className={`px-2 py-1 text-xs ${
            viewType === UserRole.HOST_CONFIG_VIEW
              ? "bg-blue-600 text-white"
              : "text-gray-300 hover:bg-gray-600"
          }`}
          onClick={() => onCodeTabChange(UserRole.HOST_CONFIG_VIEW)}
        >
          HostConfig View
        </button>
      </div>

      <CustomViewsDropdown
        activeView={viewType}
        onViewChange={onCodeTabChange}
        size="sm"
      />
    </div>
  )
}

/**
 * Status Indicators Component
 * Shows various status indicators for the editor
 */
const StatusIndicators: React.FC<{
  loading: boolean
  showTranspiledCode: boolean
  transpileStatus: TranspileStatus
  isPreviewStale: boolean
}> = ({ loading, showTranspiledCode, transpileStatus, isPreviewStale }) => {
  return (
    <>
      {/* Loading indicator */}
      {loading && (
        <span className="text-blue-400 text-sm flex items-center">
          <div className="mr-2 animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
          Loading plugin...
        </span>
      )}

      {/* Read-only indicator */}
      {showTranspiledCode && (
        <span className="text-yellow-400 text-xs flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3 w-3 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
              clipRule="evenodd"
            />
          </svg>
          Read-only
        </span>
      )}

      {/* Transpile status indicator */}
      {transpileStatus === "processing" && (
        <span className="ml-auto text-blue-400 text-sm flex items-center">
          <div className="mr-2 animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
          Processing changes...
        </span>
      )}

      {transpileStatus === "updated" && (
        <span className="ml-auto text-green-400 text-sm flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          Preview updated
        </span>
      )}

      {transpileStatus === "error" && (
        <span className="ml-auto text-red-400 text-sm flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          Transpilation error
        </span>
      )}

      {/* Preview status indicator */}
      {isPreviewStale && transpileStatus !== "processing" && (
        <span className="ml-auto text-yellow-400 text-sm flex items-center">
          <div className="mr-2 h-4 w-4 bg-yellow-400 rounded-full animate-pulse"></div>
          Preview out of sync
        </span>
      )}
    </>
  )
}

/**
 * Monaco Editor With Configuration
 * Handles setup and configuration of the Monaco editor
 */
const MonacoEditorWithConfig: React.FC<{
  activePlugin: Plugin | null
  editorState: CodeEditorState
  editorRef: React.MutableRefObject<any>
  showTranspiledCode: boolean
  onChange?: (value: string) => void
}> = ({
  activePlugin,
  editorState,
  editorRef,
  showTranspiledCode,
  onChange,
}) => {
  const { currentCode, transpiledCode, viewType } = editorState

  // Get transpiled code for current view
  const getTranspiledCodeForCurrentView = () => {
    return transpiledCode || ""
  }

  // Handler for editor mounting
  const handleEditorMount = (editor: any, monaco: any) => {
    // Store editor reference
    editorRef.current = editor

    if (DEBUG_MODE) {
      console.log(
        `[EDITOR-MOUNT] Monaco editor mounted for plugin: ${activePlugin?.id}, file: ${activePlugin?.id ? activePlugin.files.find((f) => f.type === editorState.viewType)?.id : "none"}, with content length: ${currentCode?.length || 0}`,
      )
      if (currentCode) {
        console.log(
          `[EDITOR-MOUNT] Content hash: ${currentCode.substring(0, 20).replace(/\s/g, "")}`,
        )
      }
    }

    // Register .tsx extension with TypeScript
    monaco.languages.typescript.typescriptDefaults.setExtraLibs([])
    monaco.languages.typescript.javascriptDefaults.setExtraLibs([])

    // Add React typings
    monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
      jsx: monaco.languages.typescript.JsxEmit.React,
      jsxFactory: "React.createElement",
      reactNamespace: "React",
      allowNonTsExtensions: true,
      target: monaco.languages.typescript.ScriptTarget.ES2016,
      allowJs: true,
      moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
      module: monaco.languages.typescript.ModuleKind.ESNext,
      noEmit: true,
      typeRoots: ["node_modules/@types"],
      strict: false,
    })

    // Define basic React types
    const reactDts = `
      declare module "react" {
        export = React;
      }
      
      declare namespace React {
        function createElement(type: any, props?: any, ...children: any[]): any;
        function Fragment(props: any): any;
        
        type FC<P = {}> = FunctionComponent<P>;
        interface FunctionComponent<P = {}> {
          (props: P): any;
        }
        
        type CSSProperties = any;
        
        interface HTMLAttributes<T> {
          className?: string;
          style?: CSSProperties;
          onClick?: (e: MouseEvent) => void;
          onChange?: (e: Event) => void;
          [key: string]: any;
        }
        
        interface MouseEvent {
          preventDefault(): void;
          stopPropagation(): void;
        }
        
        interface Event {
          preventDefault(): void;
          stopPropagation(): void;
          target: EventTarget;
        }
        
        interface EventTarget {
          value?: any;
          [key: string]: any;
        }
        
        interface ReactElement<P = any> {
          type: any;
          props: P;
          key: any;
        }
        
        const useState: <T>(initialState: T) => [T, (newState: T) => void];
        const useEffect: (effect: () => void | (() => void), deps?: any[]) => void;
        const useCallback: <T extends (...args: any[]) => any>(callback: T, deps?: any[]) => T;
        const useMemo: <T>(factory: () => T, deps?: any[]) => T;
        const useRef: <T>(initialValue: T) => { current: T };
        
        // Add more React types as needed...
      }
    `

    // Add the basic React typings
    monaco.languages.typescript.typescriptDefaults.addExtraLib(
      reactDts,
      "file:///node_modules/@types/react/index.d.ts",
    )

    // Get the type definitions by importing the actual types
    const eventPluginDataType = `
      settings?: {
        enabled: boolean
        [key: string]: any
      }
      state?: {
        [key: string]: any
      }
      [key: string]: any
    `

    const userPluginDataType = `
      preferences?: {
        [key: string]: any
      }
      permissions?: string[]
      state?: {
        [key: string]: any
      }
      [key: string]: any
    `

    // Add custom plugin typings using the generated definitions
    const customPluginDts = generatePluginTypeDefinitions()

    monaco.languages.typescript.typescriptDefaults.addExtraLib(
      customPluginDts,
      "file:///static/plugin-types.d.ts",
    )

    // Generate template if no code exists
    generateTemplateIfNeeded(editor, currentCode, editorState.viewType)
  }

  return (
    <MonacoEditor
      key={`${viewType}-${showTranspiledCode ? "js" : "tsx"}-${activePlugin?.id || "no-plugin"}-${activePlugin?.id ? activePlugin.files.find((f) => f.type === viewType)?.id || "no-file" : "no-file"}`}
      language={showTranspiledCode ? "javascript" : "typescript"}
      defaultPath="file:///main.tsx"
      theme="vs-dark"
      value={
        showTranspiledCode
          ? getTranspiledCodeForCurrentView() ||
            "// No transpiled code available"
          : currentCode || "// Loading..."
      }
      onChange={showTranspiledCode ? undefined : onChange}
      onMount={handleEditorMount}
      options={{
        readOnly: showTranspiledCode,
        minimap: { enabled: false },
        fontSize: 14,
        tabSize: 2,
        scrollBeyondLastLine: false,
        automaticLayout: true,
        autoIndent: "brackets",
        lineNumbersMinChars: 3,
        wordWrap: "on",
        wrappingStrategy: "advanced",
        scrollbar: {
          verticalScrollbarSize: 8,
          horizontalScrollbarSize: 8,
        },
        fixedOverflowWidgets: true,
      }}
    />
  )
}

/**
 * Toolbar Component
 * Contains the view selector and status indicators
 */
const EditorToolbar: React.FC<{
  viewType: UserRole
  onCodeTabChange: (tab: UserRole | string, file?: any) => void
  loading: boolean
  showTranspiledCode: boolean
  transpileStatus: TranspileStatus
  isPreviewStale: boolean
}> = ({
  viewType,
  onCodeTabChange,
  loading,
  showTranspiledCode,
  transpileStatus,
  isPreviewStale,
}) => {
  return (
    <div className="flex h-10 bg-gray-800 items-center px-2 border-b border-gray-700">
      <div className="flex space-x-2 w-full">
        {/* View buttons */}
        <ViewSelector viewType={viewType} onCodeTabChange={onCodeTabChange} />

        {/* Status indicators */}
        <StatusIndicators
          loading={loading}
          showTranspiledCode={showTranspiledCode}
          transpileStatus={transpileStatus}
          isPreviewStale={isPreviewStale}
        />
      </div>
    </div>
  )
}

/**
 * Presentation component for the code editor panel
 */
export const CodeEditorPanelUI: React.FC<CodeEditorPanelProps> = ({
  title,
  editorState,
  activePlugin,
  loading = false,
  isPreviewStale = false,
  transpileStatus = "idle",
  showTranspiledCode = false,
  onCodeTabChange,
  onCodeChange,
  onToggleTranspiledView,
  onRunTranspile,
  editorRef,
  className,
  onToggleVisibility,
  isVisible = true,
}) => {
  // Destructure editorState for clarity
  const { currentCode, transpiledCode } = editorState

  // State for transpilation errors
  const [transpileErrors, setTranspileErrors] = useState<PluginError[]>([])

  // Get active view type from CodeEditorState
  const viewType = editorState.viewType || UserRole.GUEST

  // Dismiss an error
  const handleDismissError = (error: PluginError) => {
    setTranspileErrors((prev) =>
      prev.filter((e) => e.timestamp !== error.timestamp),
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header with breadcrumbs and language toggle */}
      <div className="flex h-8 bg-gray-800 items-center px-3 border-b border-gray-700 justify-between">
        <EditorBreadcrumbs viewType={viewType} />
        <LanguageToggle
          showTranspiledCode={showTranspiledCode}
          onToggleTranspiledView={onToggleTranspiledView}
        />
      </div>

      {/* Toolbar with view selector and status indicators */}
      <EditorToolbar
        viewType={viewType}
        onCodeTabChange={onCodeTabChange}
        loading={loading}
        showTranspiledCode={showTranspiledCode}
        transpileStatus={transpileStatus}
        isPreviewStale={isPreviewStale}
      />

      {/* Monaco Editor */}
      <div className="h-[calc(100%-4.5rem)]">
        <MonacoEditorWithConfig
          activePlugin={activePlugin}
          editorState={editorState}
          editorRef={editorRef}
          showTranspiledCode={showTranspiledCode}
          onChange={onCodeChange}
        />
      </div>
    </div>
  )
}

// Helper function to generate template code for a new file
const generateTemplateIfNeeded = (
  editor: any,
  currentCode: string,
  viewType: UserRole,
) => {
  if (!currentCode || currentCode.trim() === "") {
    // Empty file - add a template
    const templateCode = `import React, { useEffect, useState } from "react";

/**
 * ${viewType.charAt(0).toUpperCase() + viewType.slice(1)} View
 * 
 * This component will be shown to users with the ${viewType} role.
 */
const PluginComponent: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);

  // Load data when component mounts
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Get plugin configuration
        const config = await api.getPluginConfig();
        
        // Get data from the API
        const userData = await api.getUserData();
        const eventData = await api.getEventData();
        
        setData({ config, userData, eventData });
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  return (
    <div style={{ padding: '16px', fontFamily: 'sans-serif' }}>
      <h2 style={{ color: theme.colors.primary }}>
        Hello from the ${viewType} view!
      </h2>
      
      {loading ? (
        <p>Loading data...</p>
      ) : data ? (
        <div>
          <p>User: {userData.displayName || 'Unknown'}</p>
          <p>Event: {eventData.name || 'Unknown Event'}</p>
          <pre style={{ 
            backgroundColor: '#f5f5f5', 
            padding: '8px', 
            borderRadius: '4px',
            maxHeight: '200px',
            overflow: 'auto'
          }}>
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      ) : (
        <p>No data loaded</p>
      )}
      
      <button
        style={{
          backgroundColor: theme.colors.primary,
          color: 'white',
          padding: '8px 16px',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          marginTop: '16px'
        }}
        onClick={() => {
          // Example of saving data
          api.saveEventData({ lastInteraction: new Date().toISOString() });
        }}
      >
        Save Interaction
      </button>
    </div>
  );
};

export default PluginComponent;
`

    // Update the editor with the template
    editor.setValue(templateCode)
    return templateCode
  }
  return currentCode
}

// Function to read and parse type definitions
const getPluginTypeDefinitions = () => {
  try {
    // Read the file content from the types.ts file
    const typesFilePath = path.resolve(
      __dirname,
      "../../../recoil/pluginStudio/types.ts",
    )
    const fileContent = fs.readFileSync(typesFilePath, "utf-8")

    // Extract EventPluginData and UserPluginData interfaces
    const eventPluginDataMatch = fileContent.match(
      /export interface EventPluginData {([^}]+)}/s,
    )
    const userPluginDataMatch = fileContent.match(
      /export interface UserPluginData {([^}]+)}/s,
    )

    return {
      eventPluginData: eventPluginDataMatch
        ? eventPluginDataMatch[1].trim()
        : "[key: string]: any;",
      userPluginData: userPluginDataMatch
        ? userPluginDataMatch[1].trim()
        : "[key: string]: any;",
    }
  } catch (error) {
    console.error("Error reading type definitions:", error)
    return {
      eventPluginData: "[key: string]: any;",
      userPluginData: "[key: string]: any;",
    }
  }
}

/**
 * Container component that connects to Recoil state
 */
const CodeEditorPanel: React.FC = () => {
  // Extract needed functions and state from Recoil
  const activePlugin = useRecoilValue(activePluginSelector)
  const [activeFile, setActiveFile] = useRecoilState(activeFileSelector)
  const {
    isPreviewStale,
    refreshPreview,
    setActiveViewType,
    setActiveFile: setActiveFileFromUsePlugin,
  } = usePlugins()

  // Setup editor using refactored hook
  const {
    editorRef,
    editorState,
    handleCodeChange: editorHandleCodeChange,
    toggleTranspiledView,
    showTranspiledCode,
    viewType: editorViewType,
  } = usePluginEditor()

  // Setup transpilation using new hook
  const { transpileStatus, transpileCode, debouncedTranspile } =
    useCodeTranspiler()

  // Component state declarations
  const [errors, setErrors] = useState<PluginError[]>([])

  // Map activeFile type to UserRole enum
  const getActiveRole = (): UserRole => {
    if (!activeFile?.type) return UserRole.GUEST

    // Check if it's a custom view by looking at the file name
    if (activeFile.name && isCustomView(activeFile)) {
      return UserRole.CUSTOM
    }

    switch (activeFile.type) {
      case UserRole.GUEST:
        return UserRole.GUEST
      case UserRole.HOST_EVENT_VIEW:
        return UserRole.HOST_EVENT_VIEW
      case UserRole.HOST_CONFIG_VIEW:
        return UserRole.HOST_CONFIG_VIEW
      default:
        return UserRole.GUEST
    }
  }

  // Current active view type derived from the file
  const activeViewType = getActiveRole()

  // Handle code tab changes - also updates the active file
  const handleCodeTabChange = (tab: UserRole | string, file?: any) => {
    if (!activePlugin) return

    // If it's a standard view, use setActiveViewType
    if (
      tab != UserRole.CUSTOM &&
      Object.values(UserRole).includes(tab as UserRole)
    ) {
      // Update the plugin's view type at the application level
      setActiveViewType(activePlugin.id, tab as UserRole)
    } else {
      // If it's a custom view (string), we need to find the file and select it
      const customFile = activePlugin.files?.find(
        (_file) => _file.name === file.fileName,
      )
      if (customFile) {
        // For custom views, we need to select the file directly
        console.log(`[CodeEditorPanel] Switching to custom view: ${tab}`)

        // Set the active file to the custom file
        setActiveFileFromUsePlugin(activePlugin.id, customFile.id)
      } else {
        console.warn(`[CodeEditorPanel] Custom view file not found: ${tab}`)
      }
    }
  }

  // Handle code changes
  const handleCodeChange = (newCode: string) => {
    if (activePlugin && activeFile) {
      if (DEBUG_MODE) {
        console.log(
          `[CODE-CHANGE] Handling code change for file: ${activeFile.id}, new code hash: ${newCode.substring(0, 20).replace(/\s/g, "")}`,
        )
      }

      // Update content in Recoil directly
      setActiveFile(newCode as any)

      // Trigger debounced transpilation
      debouncedTranspile(newCode)

      // Update editor state
      editorHandleCodeChange(newCode)
    }
  }

  return (
    <CodeEditorPanelUI
      editorState={{
        ...editorState,
        viewType: activeViewType,
      }}
      activePlugin={activePlugin}
      loading={false}
      isPreviewStale={isPreviewStale}
      transpileStatus={transpileStatus}
      showTranspiledCode={showTranspiledCode}
      onCodeTabChange={handleCodeTabChange}
      onCodeChange={handleCodeChange}
      onToggleTranspiledView={toggleTranspiledView}
      onRunTranspile={() => {
        if (activeFile?.content) {
          transpileCode(activeFile.content)
        }
      }}
      editorRef={editorRef}
    />
  )
}

export default CodeEditorPanel
